import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../components/layout/MainLayout';
import { useTheme } from '../context/ThemeContext';
import {
  Plus, Search, Filter, Grid3X3, List, MoreVertical,
  Calendar, User, Building2, FileText, Clock, ChevronRight,
  Scale, AlertCircle, CheckCircle2, Timer
} from 'lucide-react';

const cases = [
  { id: 1, number: 'WP/1234/2024', title: 'Sharma vs State of Delhi', client: 'Sharma Industries', court: 'Delhi High Court', nextHearing: '15 Jan 2025', status: 'active', type: 'Civil', priority: 'high' },
  { id: 2, number: 'CS/567/2024', title: 'Verma Construction Dispute', client: 'Verma & Sons', court: 'Tis Hazari Court', nextHearing: '18 Jan 2025', status: 'active', type: 'Corporate', priority: 'medium' },
  { id: 3, number: 'CRL/890/2024', title: 'State vs Gupta', client: 'Gupta Holdings', court: 'Saket Court', nextHearing: '20 Jan 2025', status: 'pending', type: 'Criminal', priority: 'high' },
  { id: 4, number: 'ARB/123/2024', title: 'ABC Ltd Arbitration', client: 'ABC Corporation', court: 'DIAC', nextHearing: '25 Jan 2025', status: 'active', type: 'Arbitration', priority: 'low' },
  { id: 5, number: 'FAM/456/2024', title: 'Custody Matter', client: 'Private Client', court: 'Family Court', nextHearing: '28 Jan 2025', status: 'active', type: 'Family', priority: 'high' },
  { id: 6, number: 'WP/789/2023', title: 'Environmental Petition', client: 'Green Earth NGO', court: 'Supreme Court', nextHearing: '30 Jan 2025', status: 'disposed', type: 'PIL', priority: 'medium' },
];

const statusColors = {
  active: 'from-green-500 to-emerald-500',
  pending: 'from-yellow-500 to-amber-500',
  disposed: 'from-gray-500 to-slate-500',
};

const priorityColors = {
  high: 'bg-red-500/20 text-red-500',
  medium: 'bg-yellow-500/20 text-yellow-500',
  low: 'bg-green-500/20 text-green-500',
};

const Matters = () => {
  const { isDark } = useTheme();
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);

  return (
    <MainLayout title="Matters / Cases" subtitle="Manage all your legal matters">
      {/* Action Bar */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-6"
      >
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search cases..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-3 w-80 rounded-xl input-glass"
            />
          </div>
          
          {/* Filter */}
          <motion.button
            onClick={() => setFilterOpen(!filterOpen)}
            className="flex items-center gap-2 px-4 py-3 rounded-xl glass-card"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Filter className="w-5 h-5" />
            <span>Filters</span>
          </motion.button>
        </div>

        <div className="flex items-center gap-4">
          {/* View Toggle */}
          <div className="flex items-center gap-1 p-1 rounded-xl glass-card">
            <motion.button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-500 text-white' : ''}`}
              whileTap={{ scale: 0.95 }}
            >
              <Grid3X3 className="w-5 h-5" />
            </motion.button>
            <motion.button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-500 text-white' : ''}`}
              whileTap={{ scale: 0.95 }}
            >
              <List className="w-5 h-5" />
            </motion.button>
          </div>

          {/* Add New */}
          <motion.button
            className="flex items-center gap-2 px-5 py-3 rounded-xl btn-primary"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus className="w-5 h-5" />
            <span>New Matter</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Cases', value: '24', icon: Scale, color: 'blue' },
          { label: 'Active', value: '18', icon: CheckCircle2, color: 'green' },
          { label: 'Pending', value: '4', icon: Timer, color: 'yellow' },
          { label: 'Disposed', value: '2', icon: FileText, color: 'gray' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="glass-card p-4 flex items-center gap-4"
          >
            <div className={`p-3 rounded-xl bg-${stat.color}-500/20`}>
              <stat.icon className={`w-5 h-5 text-${stat.color}-500`} />
            </div>
            <div>
              <p className="text-2xl font-bold">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Cases Grid/List */}
      <AnimatePresence mode="wait">
        {viewMode === 'grid' ? (
          <motion.div
            key="grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-3 gap-6"
          >
            {cases.map((caseItem, index) => (
              <motion.div
                key={caseItem.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.02, y: -5 }}
                onClick={() => setSelectedCase(caseItem)}
                className="glass-card p-6 cursor-pointer relative overflow-hidden group"
              >
                {/* Status Indicator */}
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${statusColors[caseItem.status]}`} />
                
                {/* Hover Glow */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity"
                />
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-xs text-blue-500 font-semibold mb-1">{caseItem.number}</p>
                      <h3 className="font-semibold text-lg mb-1 line-clamp-1">{caseItem.title}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${priorityColors[caseItem.priority]}`}>
                        {caseItem.priority} priority
                      </span>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="p-2 rounded-lg hover:bg-gray-500/10"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </motion.button>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <User className="w-4 h-4" />
                      <span>{caseItem.client}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Building2 className="w-4 h-4" />
                      <span>{caseItem.court}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      <span>Next: {caseItem.nextHearing}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-500/20">
                    <span className="px-3 py-1 text-xs rounded-full bg-gray-500/10">{caseItem.type}</span>
                    <motion.button
                      className="flex items-center gap-1 text-sm text-blue-500"
                      whileHover={{ x: 5 }}
                    >
                      <span>View Details</span>
                      <ChevronRight className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="glass-card overflow-hidden"
          >
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8B] text-white">
                  <th className="px-6 py-4 text-left">Case Number</th>
                  <th className="px-6 py-4 text-left">Title</th>
                  <th className="px-6 py-4 text-left">Client</th>
                  <th className="px-6 py-4 text-left">Court</th>
                  <th className="px-6 py-4 text-left">Next Hearing</th>
                  <th className="px-6 py-4 text-left">Status</th>
                  <th className="px-6 py-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((caseItem, index) => (
                  <motion.tr
                    key={caseItem.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="border-b border-gray-500/10 hover:bg-blue-500/5 cursor-pointer"
                    onClick={() => setSelectedCase(caseItem)}
                  >
                    <td className="px-6 py-4 font-semibold text-blue-500">{caseItem.number}</td>
                    <td className="px-6 py-4">{caseItem.title}</td>
                    <td className="px-6 py-4">{caseItem.client}</td>
                    <td className="px-6 py-4">{caseItem.court}</td>
                    <td className="px-6 py-4">{caseItem.nextHearing}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs rounded-full bg-gradient-to-r ${statusColors[caseItem.status]} text-white`}>
                        {caseItem.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        className="p-2 rounded-lg hover:bg-gray-500/10"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </motion.button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        )}
      </AnimatePresence>
    </MainLayout>
  );
};

export default Matters;
